import { isActiveGroup } from 'sonata-static-storage/lib/helpers/itemsGrouping.helpers';

import { familyAdapters } from './familyAdapters';
import { toId } from './groupIds';
import { getGroupCapabilities, isDefaultGroup, isEditableGroup, isVisibleGroup } from './groupRules';

import type { GroupableItem, GroupFamily, GroupLike, GroupView } from './grouping.types';

/** UI ids of the non-deleted items belonging to a group. */
export function getMemberIds(family: GroupFamily, items: GroupableItem[], groupId: string): string[] {
  const { itemId } = familyAdapters[family];
  return items.filter(item => !item.deleted && (item.groups ?? []).some(id => toId(id) === groupId)).map(itemId);
}

/**
 * Groups in use in the current map. Entity-scoped families (screens, locations) own their groups, so all of them
 * are returned untouched. User-level groups (venues) are shared by every campaign of the user: only the active ones
 * referenced by a non-deleted item of this map, or created in this session (not persisted on load), are in use.
 */
export function selectGroupsInUse<G extends GroupLike>(
  family: GroupFamily,
  groups: G[],
  items: GroupableItem[],
  existedOnLoad: ReadonlySet<string>
): G[] {
  if (!familyAdapters[family].userLevel) {
    return groups;
  }
  const referenced = new Set(items.filter(item => !item.deleted).flatMap(item => (item.groups ?? []).map(toId)));
  return groups.filter(group => {
    const id = toId(group._id);
    return isActiveGroup(group) && (referenced.has(id) || !existedOnLoad.has(id));
  });
}

const countMembers = (items: GroupableItem[]): Map<string, number> => {
  const counts = new Map<string, number>();
  items
    .filter(item => !item.deleted)
    .forEach(item => {
      new Set((item.groups ?? []).map(toId)).forEach(id => {
        counts.set(id, (counts.get(id) ?? 0) + 1);
      });
    });
  return counts;
};

const toGroupView = (family: GroupFamily, group: GroupLike, counts: Map<string, number>): GroupView => {
  const id = toId(group._id);
  return {
    capabilities: getGroupCapabilities(family, group),
    color: group.color,
    editable: isEditableGroup(family, group),
    icon: group.icon,
    id,
    memberCount: counts.get(id) ?? 0,
    name: group.name
  };
};

/** Visible groups of a family projected for the UI, with capabilities and member counts. */
export function toGroupViews(family: GroupFamily, groups: GroupLike[], items: GroupableItem[]): GroupView[] {
  const counts = countMembers(items);
  return groups.filter(group => isVisibleGroup(family, group)).map(group => toGroupView(family, group, counts));
}

/**
 * Groups offered in the list filters: the screens default group (first, flagged, only while it has members) plus the
 * visible groups.
 */
export function toFilterGroupViews(family: GroupFamily, groups: GroupLike[], items: GroupableItem[]): GroupView[] {
  const counts = countMembers(items);
  const defaults = groups
    .filter(group => isDefaultGroup(family, group) && (counts.get(toId(group._id)) ?? 0) > 0)
    .map((group): GroupView => ({ ...toGroupView(family, group, counts), isDefault: true }));
  return [...defaults, ...toGroupViews(family, groups, items)];
}

/** Whether some non-deleted item belongs to none of the given (visible) groups. */
export function hasUngroupedItems(groups: GroupView[], items: GroupableItem[]): boolean {
  const groupIds = new Set(groups.map(group => group.id));
  return items.some(item => !item.deleted && !(item.groups ?? []).some(id => groupIds.has(toId(id))));
}

/**
 * Groups offered when assigning groups to creatives: active ones, leaving out the screens default group once no
 * non-deleted item belongs to it.
 */
export function selectCreativeGroups<G extends GroupLike>(
  family: GroupFamily,
  groups: G[],
  items: GroupableItem[]
): G[] {
  const counts = countMembers(items);
  return groups.filter(
    group => isActiveGroup(group) && (!isDefaultGroup(family, group) || (counts.get(toId(group._id)) ?? 0) > 0)
  );
}

/** Groups a brand-new item starts in: screens go to the `default` group (if any), other families to none. */
export function initialGroupIds(family: GroupFamily, groups: GroupLike[]): string[] {
  const defaultGroupId = familyAdapters[family].defaultGroupId(groups);
  return defaultGroupId ? [defaultGroupId] : [];
}
